// components/ArticleCreate.tsx
// Article creation with draft auto-save

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useFileUpload } from '../firebase/hooks';
import { useDrafts } from '../firebase/useDrafts';
import { useAuth } from '../contexts/AuthContext';
import { PostCategory } from '../types';
import './VideoUpload.css';
import { v4 as uuidv4 } from 'uuid';
import { RichTextEditor } from './RichTextEditor/RichTextEditor';
import { sanitizeHtml, createSafePreview } from '../utils/htmlSanitizer';
import { countWords } from '../utils/wordCounter';

export const ArticleCreate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { uploadFile, uploadProgress, resetProgress } = useFileUpload();
    const { saveDraft, loadDraft, deleteDraft, autoSaveStatus } = useDrafts('article');

    const [draftId, setDraftId] = useState<string | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState<PostCategory>('writing-tips');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Debug auth state
    useEffect(() => {
        console.log('Current user:', currentUser);
        console.log('User authenticated:', !!currentUser);
        console.log('User email:', currentUser?.email);
        console.log('User ID:', currentUser?.uid);
    }, [currentUser]);

    // MOVED: Define handleAutoSave BEFORE the useEffect that uses it
    const handleAutoSave = useCallback(async () => {
        if (!title && !content) return;

        const id = await saveDraft({
            title,
            content,
            category,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);

        if (id && !draftId) {
            setDraftId(id);
        }
    }, [title, content, category, thumbnailFile, draftId, saveDraft]);

    // Load draft if resuming
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const resumeDraftId = params.get('draft');

        if (resumeDraftId) {
            loadDraft(resumeDraftId).then(draft => {
                if (draft) {
                    setDraftId(draft.id);
                    setTitle(draft.title || '');
                    setContent(draft.content || '');
                    setCategory(draft.category);
                }
            });
        }
    }, [location.search, loadDraft]);

    // Auto-save every 30 seconds
    useEffect(() => {
        if (title || content) {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }

            autoSaveTimerRef.current = setTimeout(() => {
                handleAutoSave();
            }, 30000);
        }

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [title, content, category, handleAutoSave]); // NOW handleAutoSave is defined

    const handleSaveDraft = async () => {
        const id = await saveDraft({
            title,
            content,
            category,
            thumbnailFileName: thumbnailFile?.name,
        }, draftId || undefined);

        if (id) {
            alert('Draft saved successfully!');
            navigate('/dashboard');
        }
    };

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Thumbnail must be less than 5MB');
                return;
            }
            setThumbnailFile(file);
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check authentication first
        if (!currentUser) {
            setError('You must be logged in to publish articles');
            return;
        }

        if (!title.trim() || !content.trim()) {
            setError('Please fill in all required fields');
            return;
        }
        if (title.length < 5 || title.length > 100) {
            setError('Title must be between 5 and 100 characters');
            return;
        }

        // Sanitize HTML content
        const sanitizedContent = sanitizeHtml(content);

        // Get word count
        const { words } = countWords(sanitizedContent);

        // Validate word count (instead of character count)
        if (words < 50) {
            setError('Article must be at least 50 words');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            let thumbnailUrl = '';

            if (thumbnailFile) {
                const thumbnailPath = `barkBoard/thumbnails/${Date.now()}_${thumbnailFile.name}`;
                thumbnailUrl = await uploadFile(thumbnailFile, thumbnailPath);
            }

            const docId = uuidv4(); // Generate once
            const postData: any = {
                id: docId, // Use same ID
                type: 'article',
                postType: 'admin',
                title: title.trim(),
                caption: createSafePreview(sanitizedContent, 200), // Generate plain text preview
                articleContent: sanitizedContent,                   // Store HTML
                articleContentPlain: createSafePreview(sanitizedContent, 5000), // Full plain text
                contentFormat: 'html' as const,                     // Format indicator
                category,
                authorName: 'Omore, the GOAT',
                authorEmail: currentUser.email || '',
                authorId: currentUser.uid,
                createdAt: serverTimestamp(),
                publishedAt: serverTimestamp(),
                status: 'published' as const,
                isActive: true,
                viewCount: 0,
                complimentCounts: {
                    creative: 0,
                    funny: 0,
                    inspiring: 0,
                    smart: 0,
                    cool: 0,
                    brave: 0,
                    original: 0,
                    relatable: 0,
                    amazing: 0,
                    wholesome: 0,
                    artistic: 0,
                    magical: 0,
                    legendary: 0,
                    genius: 0,
                    epic: 0,
                    champion: 0,
                    total: 0
                },
                wordCount: words,                                   // NEW: Word count
                readingTime: Math.ceil(words / 200),               // NEW: Reading time (200 words/min)
            };

            // Only add thumbnailUrl if it exists
            if (thumbnailUrl) {
                postData.thumbnailUrl = thumbnailUrl;
            }

            // Use setDoc instead of addDoc
            await setDoc(doc(db, 'barkBoard', 'posts', 'items', docId), postData);

            if (draftId) {
                await deleteDraft(draftId);
            }

            setSuccess(true);
            resetProgress();

            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.message || 'Failed to publish article. Please try again.');
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        navigate('/dashboard');
    };

    if (success) {
        return (
            <div className="upload-container">
                <div className="success-card">
                    <div className="success-icon">✅</div>
                    <h2>Article Published Successfully!</h2>
                    <p>Your article is now live on the Spotlight.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="upload-container">
            <div className="upload-card">
                <div className="upload-header">
                    <h1>Write Article</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {autoSaveStatus === 'saving' && (
                            <span style={{ fontSize: '12px', color: '#718096' }}>💾 Saving...</span>
                        )}
                        {autoSaveStatus === 'saved' && (
                            <span style={{ fontSize: '12px', color: '#48bb78' }}>✓ Draft saved</span>
                        )}
                        <button onClick={handleCancel} className="close-button">×</button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="upload-form">
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="form-section">
                        <label className="form-label" htmlFor="title">Title *</label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., How to Create Compelling Characters"
                            maxLength={100}
                            disabled={isUploading}
                            className="text-input"
                        />
                        <span className="char-count">{title.length}/100</span>
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="content">Article Content *</label>
                        <RichTextEditor
                            value={content}
                            onChange={setContent}
                            placeholder="Write your article here... Share tips, insights, and inspiration for young writers."
                            disabled={isUploading}
                            showWordCount={true}
                            minHeight="300px"
                            maxHeight="600px"
                            onImageUpload={async (file) => {
                                // Upload inline images to Firebase Storage
                                const path = `barkBoard/article-images/${Date.now()}_${file.name}`;
                                return await uploadFile(file, path);
                            }}
                        />
                    </div>

                    <div className="form-section">
                        <label className="form-label" htmlFor="category">Category *</label>
                        <select
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as PostCategory)}
                            disabled={isUploading}
                            className="select-input"
                        >
                            <option value="writing-tips">Writing Tips</option>
                            <option value="story-inspiration">Story Inspiration</option>
                            <option value="creative-tools">Creative Tools</option>
                            <option value="character-development">Character Development</option>
                            <option value="world-building">World Building</option>
                            <option value="community-spotlight">Community Spotlight</option>
                        </select>
                    </div>

                    <div className="form-section">
                        <label className="form-label">Thumbnail Image (Optional)</label>
                        <div className="file-upload-area">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                disabled={isUploading}
                                id="thumbnail-file"
                            />
                            <label htmlFor="thumbnail-file" className="file-upload-label">
                                {thumbnailFile ? (
                                    <span>🖼️ {thumbnailFile.name}</span>
                                ) : (
                                    <span>Click to select thumbnail (JPG, PNG - Max 5MB)</span>
                                )}
                            </label>
                        </div>
                    </div>

                    {uploadProgress && uploadProgress.status === 'uploading' && (
                        <div className="progress-section">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress.progress}%` }}
                                />
                            </div>
                            <p className="progress-text">
                                Uploading {uploadProgress.fileName}... {uploadProgress.progress}%
                            </p>
                        </div>
                    )}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={handleSaveDraft}
                            className="cancel-button"
                            disabled={isUploading}
                            style={{ background: '#667eea', color: 'white' }}
                        >
                            💾 Save Draft
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="cancel-button"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="publish-button"
                            disabled={isUploading || !title || !content}
                        >
                            {isUploading ? 'Publishing...' : 'Publish Article'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};